/**
 * 投放计划管理 —— 前后端共享 Zod 契约（ADM-AP-*，全局模块）
 *
 * 契约源：dev-docs `02-API契约/AF-投放计划.md` + `03-数据字典/120-投放落地页.md` §5/§6。
 * 覆盖 ADM-AP-S001（计划 CRUD）+ S002（三态切换）+ S003（关联落地页整组替换）。
 *
 * **全局模块**：不按 `X-App-Id` 切分，计划 body / query 不接收 `app_id`；§5 Web 链接 body 的 app_id 仅是投放目标。
 * **不设角色分级**：读写同为 viewer+（豁免 operator 门槛），仍要求 `mfa_verified=true`。
 * **无新增业务码**：只用系统码 `VALIDATION_FAILED` / `RESOURCE_NOT_FOUND` / `RESOURCE_CONFLICT`，
 * 拒绝原因经 `details.reason` 区分（取值见下方常量）。
 */

import { z } from 'zod';

/** 投放状态闭集（AF §3）：运营手动切换、任意方向，不由 `starts_at` / `ends_at` 驱动 */
export const AD_PLAN_STATUSES = ['NOT_STARTED', 'RUNNING', 'ENDED'] as const;
export type AdPlanStatus = (typeof AD_PLAN_STATUSES)[number];

/**
 * §4 关联替换的两个拒绝原因（`details.reason`，`VALIDATION_FAILED` 下）。
 * 两者必须分开：前者是「这个 id 根本不存在」、后者是「存在但不同书或不同渠道」——
 * 合成一个会让运营看不出该去建落地页还是该换候选。
 */
export const AD_PLAN_LANDING_PAGE_NOT_FOUND = 'AD_PLAN_LANDING_PAGE_NOT_FOUND';
export const AD_PLAN_LANDING_PAGE_MISMATCH = 'AD_PLAN_LANDING_PAGE_MISMATCH';

/** AE §4.2 漂移守卫的拒绝原因（`RESOURCE_CONFLICT` 下，落地页被计划关联期间拒改书/渠道） */
export const LANDING_PAGE_LINKED_TO_AD_PLAN = 'LANDING_PAGE_LINKED_TO_AD_PLAN';

/**
 * 参与**身份比对**的 uuid：校验后归一成小写再交给 service。
 *
 * `z.string().uuid()` 收大小写混写（`0190A2…` 是合法 uuid），而 Postgres 返回的 uuid 恒小写。
 * service 侧 `books.has(novel_id)` / `foundIds.has(id)` 是**字符串**比对：不归一时，库里明明查到了
 * 这一行，比对却落空 —— 一本存在的书被判成「书籍不存在」、一个存在的落地页被列进 `missing`。
 * 同理 `landing_page_ids` 的重复检测会漏掉同一个 id 的异写，两条重复值一路走到 INSERT 撞主键。
 *
 * 归一放在契约层而不是 service：这样任何新增的消费方拿到的都已经是规范形，不必各自记得转一次。
 */
const IdentityUuidSchema = z
  .string()
  .uuid()
  .transform((s) => s.toLowerCase());

/** ISO 8601 datetime；`null` 表示不设该端（契约允许单边为空）*/
const NullableDatetimeSchema = z.string().datetime().nullable();

/**
 * 起止时间的先后校验（两者都非空时才比较）—— 与 DB CHECK `ad_plans_period_order` 同口径。
 * 单边为空不校验：契约明写「两者都非空时须 `ends_at >= starts_at`」。
 */
function periodOrderOk(v: { starts_at?: string | null; ends_at?: string | null }): boolean {
  if (!v.starts_at || !v.ends_at) return true;
  return Date.parse(v.ends_at) >= Date.parse(v.starts_at);
}
const PERIOD_ORDER_ISSUE = {
  message: 'ends_at must be greater than or equal to starts_at',
  path: ['ends_at'],
};

export const CreateAdPlanRequestSchema = z
  .object({
    /** 投放书籍；须对应未软删 `novels` 行（服务端校验）。**创建后不可改** */
    novel_id: IdentityUuidSchema,
    /** 单选渠道，闭集 = AE §2 白名单（服务端按白名单校验）。**创建后不可改** */
    media_source: z.string().min(1).max(256),
    name: z.string().min(1).max(100),
    starts_at: NullableDatetimeSchema.optional(),
    ends_at: NullableDatetimeSchema.optional(),
    note: z.string().max(2000).nullable().optional(),
  })
  .strict()
  .refine(periodOrderOk, PERIOD_ORDER_ISSUE);
export type CreateAdPlanRequest = z.infer<typeof CreateAdPlanRequestSchema>;

/**
 * 改计划（AF §2）：只可改 `name` / `starts_at` / `ends_at` / `note`。
 *
 * **`.strict()` 是这里的关键闸**：契约要求 body 携带 `novel_id` / `media_source` / `status` 一律
 * `VALIDATION_FAILED`（前两者创建后不可改、状态只经 §3）。用 `.strict()` 让未知键直接被 Zod 拒，
 * 比在 service 里逐个判更不容易漏 —— 以后契约加不可改字段时也自动覆盖。
 */
export const UpdateAdPlanRequestSchema = z
  .object({
    name: z.string().min(1).max(100).optional(),
    starts_at: NullableDatetimeSchema.optional(),
    ends_at: NullableDatetimeSchema.optional(),
    note: z.string().max(2000).nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one mutable field is required' })
  .refine(periodOrderOk, PERIOD_ORDER_ISSUE);
export type UpdateAdPlanRequest = z.infer<typeof UpdateAdPlanRequestSchema>;

/** 三态切换（AF §3）：任意方向，目标值与当前值相同时服务端不改行、不落 audit */
export const UpdateAdPlanStatusRequestSchema = z
  .object({ status: z.enum(AD_PLAN_STATUSES) })
  .strict();
export type UpdateAdPlanStatusRequest = z.infer<typeof UpdateAdPlanStatusRequestSchema>;

/**
 * 关联落地页整组替换（AF §4）：传本计划关联的**全集**，不做单条增量；空数组 = 清空。
 * 重复值由服务端判 `VALIDATION_FAILED`（这里不 dedupe —— 静默去重会让运营以为提交生效了）。
 */
export const ReplaceAdPlanLandingPagesRequestSchema = z
  .object({ landing_page_ids: z.array(IdentityUuidSchema) })
  .strict();
export type ReplaceAdPlanLandingPagesRequest = z.infer<
  typeof ReplaceAdPlanLandingPagesRequestSchema
>;

/** 列表 query 过滤（均可选、AND 关系）*/
export const ListAdPlansQuerySchema = z.object({
  novel_id: z.string().uuid().optional(),
  media_source: z.string().min(1).max(256).optional(),
  status: z.enum(AD_PLAN_STATUSES).optional(),
});
export type ListAdPlansQuery = z.infer<typeof ListAdPlansQuerySchema>;

/** 详情里的关联落地页摘要（AF §2，按关联建立时刻升序）*/
export type AdPlanLandingPageSummary = {
  id: string;
  task_id: string;
  template_id: string;
  generate_status: string;
  publish_status: string | null;
  published_url: string | null;
};

/** 列表行（AF §2）：书籍摘要 admin 直连读 `novels` 取值，界面不展示 uuid */
export type AdPlanListItem = {
  id: string;
  novel_id: string;
  /** 书籍摘要；书籍行不存在时三项为 null（逻辑引用、无 FK，行可能已被软删）*/
  book_code: string | null;
  title: string | null;
  locale: string | null;
  media_source: string;
  name: string;
  status: AdPlanStatus;
  starts_at: string | null;
  ends_at: string | null;
  landing_page_count: number;
  created_at: string;
  updated_at: string;
};

/** 详情（AF §2）= 列表行全字段 + `note` + `landing_pages[]` */
export type AdPlanDetail = AdPlanListItem & {
  note: string | null;
  landing_pages: AdPlanLandingPageSummary[];
  web_links: AdPlanWebLink[];
};

/** Web 链接只接受登记参数，不能由客户端指定地址或创建后改目标（AF §5）。 */
export const WEB_LINK_UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;
const UtmFields = {
  utm_source: z.string().max(256).nullable().optional(),
  utm_medium: z.string().max(256).nullable().optional(),
  utm_campaign: z.string().max(256).nullable().optional(),
  utm_term: z.string().max(256).nullable().optional(),
  utm_content: z.string().max(256).nullable().optional(),
};
export const WebLinkUuidSchema = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/,
    '必须为 canonical UUID v7',
  );
export const CreateAdPlanWebLinkSchema = z
  .object({ app_id: WebLinkUuidSchema, ...UtmFields })
  .strict();
export const UpdateAdPlanWebLinkSchema = z
  .object({ is_active: z.boolean().optional(), ...UtmFields })
  .strict()
  .refine((v) => Object.keys(v).length > 0, '至少提供一个待更新字段');
export type CreateAdPlanWebLinkRequest = z.infer<typeof CreateAdPlanWebLinkSchema>;
export type UpdateAdPlanWebLinkRequest = z.infer<typeof UpdateAdPlanWebLinkSchema>;
export type AdPlanWebLink = {
  id: string;
  ad_plan_id: string;
  app_id: string;
  book_code: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  url: string;
};
